import {CommonModule} from '@angular/common';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {finalize, forkJoin, Subject, takeUntil} from 'rxjs';
import {MaterialModule} from '../../../material.module';
import {HubspotIntegrationStatus, WebinarAdditionalProperties, WebinarSettings, WebinarSettingsInput} from '../../../models/webinar.model';
import {WebinarAdditionalPropertiesComponent} from './additional-properties/additional-properties.component';
import {ToastService} from '../../../services/layout/toast.service';
import {WebinarsService} from '../../../services/webinars/webinars.service';
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';

const HUBSPOT_REGISTRATION_PROPERTY_NAME = 'shine_webinars_latest_registration_id';

@Component({
    selector: 'app-webinar-settings',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MaterialModule, BreadcrumbsComponent, WebinarAdditionalPropertiesComponent],
    templateUrl: './webinar-settings.component.html',
    styleUrls: ['./webinar-settings.component.scss'],
    host: {class: 'webinars-m3 webinars-shell'},
})
export class WebinarSettingsComponent implements OnInit, OnDestroy {
    private readonly fb = inject(FormBuilder);
    private readonly webinarsService = inject(WebinarsService);
    private readonly toastService = inject(ToastService);
    readonly breadcrumbs = [{label: 'Shine Webinars', route: '/webinars'}, {label: 'Settings'}];
    form!: FormGroup;
    status: HubspotIntegrationStatus | null = null;
    settings: WebinarSettings | null = null;

    isLoading = false;
    isSaving = false;
    isProvisioning = false;
    isBrandingExpanded = false;
    logoFileName = '';
    faviconFileName = '';
    logoPreviewUrl: string | null = null;
    faviconPreviewUrl: string | null = null;

    private destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.buildForm();
        this.loadAll(false);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    refresh(): void {
        this.loadAll(true);
    }

    get showProvisionButton(): boolean {
        return !this.hasProvisionedRegistrationProperty();
    }

    get canProvisionRegistration(): boolean {
        return Boolean(this.status?.connected && this.status?.supportsSetupValidation);
    }

    save(): void {
        if (!this.form || this.form.disabled || this.form.invalid || this.isSaving) {this.form?.markAllAsTouched(); return;}
        const hubspotEnabled = Boolean(this.form.get('hubspotEnabled')?.value);
        const branding = this.buildBrandingPayload();

        const payload: WebinarSettingsInput = {
            branding,
            hubspot: {
                enabled: hubspotEnabled,
                ...(this.form.get('additionalProperties')?.dirty ? {additionalProperties: this.form.get('additionalProperties')?.value as WebinarAdditionalProperties | null} : {}),
            },
        };

        this.isSaving = true;
        this.webinarsService
            .updateWebinarSettings(payload)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isSaving = false;
                }),
            )
            .subscribe({
                next: (settings) => {
                    this.settings = settings;
                    this.patchForm(settings);
                    this.setFormPristine();
                    this.toastService.showSuccess('Webinar settings saved', 2500);
                },
                error: (err) => {
                    console.error('Failed to save webinar settings', err);
                    const code = err?.error?.code;
                    const message = code === 'connection_changed_revalidate' ? 'The HubSpot connection changed. Refresh and review additional properties.'
                        : code === 'invalid_properties' || code === 'invalid_rows' || code === 'invalid_configuration' ? 'Check the additional property names and values for each action.'
                        : 'Could not save webinar settings';
                    this.toastService.showError(message, 3500);
                },
            });
    }

    provisionHubspotRegistration(): void {
        if (!this.canProvisionRegistration || this.isProvisioning) return;
        this.isProvisioning = true;
        this.webinarsService
            .provisionHubspotWebinars()
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isProvisioning = false;
                }),
            )
            .subscribe({
                next: (validation) => {
                    if (validation?.ok) {
                        this.toastService.showSuccess('HubSpot registration property provisioned', 3000);
                    } else {
                        this.toastService.showInfo('HubSpot provisioning completed with warnings', 4000);
                    }
                    this.loadAll(false);
                },
                error: (err) => {
                    console.error('Failed to provision HubSpot registration property', err);
                    this.toastService.showError('Could not provision HubSpot registration property', 3500);
                },
            });
    }

    private buildForm(): void {
        this.form = this.fb.group({
            hubspotEnabled: [{value: false, disabled: true}],
            brandingLogoUrl: [''],
            brandingFaviconUrl: [''],
            additionalProperties: [null as WebinarAdditionalProperties | null],
        });
    }

    private buildBrandingPayload(): WebinarSettingsInput['branding'] {
        const raw = this.form?.value;
        const branding = {
            logoUrl: raw?.brandingLogoUrl?.trim() || null,
            faviconUrl: raw?.brandingFaviconUrl?.trim() || null,
        };

        const hasValue = Object.values(branding).some((v) => Boolean(v));
        return hasValue ? branding : null;
    }

    applyPreset(_kind: 'dark' | 'light'): void {
        // Presets removed with simplified branding controls
    }

    onLogoFileSelected(event: Event): void {
        this.handleFileSelection(event, 'brandingLogoUrl', 'logoFileName');
        this.logoPreviewUrl = this.form.get('brandingLogoUrl')?.value || this.logoPreviewUrl;
    }

    onFaviconFileSelected(event: Event): void {
        this.handleFileSelection(event, 'brandingFaviconUrl', 'faviconFileName');
        this.faviconPreviewUrl = this.form.get('brandingFaviconUrl')?.value || this.faviconPreviewUrl;
    }

    private handleFileSelection(event: Event, controlName: string, nameField: 'logoFileName' | 'faviconFileName'): void {
        const input = event.target as HTMLInputElement | null;
        const file = input?.files && input.files.length ? input.files[0] : null;
        if (!file) {
            this.form.get(controlName)?.setValue('');
            this[nameField] = '';
            if (controlName === 'brandingLogoUrl') this.logoPreviewUrl = null;
            if (controlName === 'brandingFaviconUrl') this.faviconPreviewUrl = null;
            return;
        }
        this[nameField] = file.name;
        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === 'string' ? reader.result : '';
            this.form.get(controlName)?.setValue(result);
            if (controlName === 'brandingLogoUrl') this.logoPreviewUrl = result;
            if (controlName === 'brandingFaviconUrl') this.faviconPreviewUrl = result;
            this.form.markAsDirty();
        };
        reader.readAsDataURL(file);
    }

    private loadAll(showToast: boolean): void {
        this.isLoading = true;
        forkJoin({
            status: this.webinarsService.getHubspotStatus(),
            settings: this.webinarsService.getWebinarSettings(),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({status, settings}) => {
                    this.status = status;
                    this.settings = settings;
                    this.patchForm(settings);
                    this.toggleFormAvailability(status?.connected === true);
                    this.isLoading = false;
                    if (showToast) {
                        this.toastService.showSuccess('HubSpot status refreshed', 2000);
                    }
                },
                error: (err) => {
                    console.error('Failed to load webinar settings context', err);
                    this.isLoading = false;
                    this.toggleFormAvailability(false);
                    this.toastService.showError('Unable to load webinar settings', 3500);
                },
            });
    }

    private patchForm(settings: WebinarSettings | null): void {
        if (!this.form) return;
        const hubspotEnabled = Boolean(settings?.hubspot?.enabled);
        const branding = settings?.branding || null;

        this.form.patchValue(
            {
                hubspotEnabled,
                brandingLogoUrl: branding?.logoUrl || '',
                brandingFaviconUrl: branding?.faviconUrl || '',
                additionalProperties: settings?.hubspot?.additionalProperties ?? null,
            },
            {emitEvent: false},
        );

        this.logoPreviewUrl = branding?.logoUrl || null;
        this.faviconPreviewUrl = branding?.faviconUrl || null;

        this.setFormPristine();
    }

    private toggleFormAvailability(canEdit: boolean): void {
        if (!this.form) return;
        const hubspotControls = ['hubspotEnabled', 'additionalProperties'];
        hubspotControls.forEach((name) => {
            const control = this.form.get(name);
            if (!control) return;
            if (canEdit) {
                control.enable({emitEvent: false});
            } else {
                control.disable({emitEvent: false});
            }
        });
    }

    private setFormPristine(): void {
        if (!this.form) return;
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.form.updateValueAndValidity({emitEvent: false});
    }

    private hasProvisionedRegistrationProperty(): boolean {
        const registrationProperty =
            this.settings?.hubspot?.registrationPropertyName || '';
        return (
            registrationProperty.trim().toLowerCase() ===
            HUBSPOT_REGISTRATION_PROPERTY_NAME
        );
    }

}
